import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BarChart3, Building2, CalendarDays, ChevronDown, Database, FileStack, FileText, Home,
  Monitor, Newspaper, PanelLeftClose, PanelLeftOpen, Phone, PieChart, Settings, Smartphone, Trophy, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CampanellaNotifiche } from "@/components/CampanellaNotifiche";
import { MenuMobile } from "@/components/MenuMobile";
import { api } from "@/lib/api";
import { applicaPalette } from "@/lib/palette";

const NAV = [
  { to: "/", label: "Home", icon: Home, exact: true, pagina: "home" },
  { to: "/giornaliero", label: "Pratiche giornaliere", icon: CalendarDays, pagina: "giornaliero" },
  { to: "/analisi-portafoglio", label: "Analisi Portafoglio", icon: PieChart, pagina: "analisi_portafoglio" },
  { to: "/pratiche", label: "Pratiche", icon: FileStack, pagina: "pratiche" },
  { to: "/news", label: "News", icon: Newspaper, pagina: "news" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, pagina: "impostazioni" },
];

// sfondo delle pagine: stessa sfumatura della schermata di ingresso (menu e barra restano bianchi)
export const SFONDO_PAGINE = "radial-gradient(55% 40% at 8% 96%, rgba(20,40,75,0.16) 0%,"
  + " rgba(20,40,75,0) 72%), radial-gradient(45% 32% at 95% 4%, rgba(200,16,46,0.05) 0%,"
  + " rgba(200,16,46,0) 74%), linear-gradient(125deg, #E7EBF2 0%, #F1F4F8 42%, #F8FAFC 100%)";

export default function Layout({ canale = "lungo", onCambiaCanale = null }) {
  const { user, can, logout } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [comms, setComms] = useState([]);
  const [commOpen, setCommOpen] = useState(false);
  const [commAperti, setCommAperti] = useState({});
  const [dealerOpen, setDealerOpen] = useState(false);
  const [homeOpen, setHomeOpen] = useState(true);
  const [setOpenState, setSetOpen] = useState(false);
  const [vociCustom, setVociCustom] = useState([]);
  const [agenzieCorto, setAgenzieCorto] = useState([]);
  const [agenzieOpen, setAgenzieOpen] = useState(false);
  const [classificheOpen, setClassificheOpen] = useState(false);
  const [impostazioniOpen, setImpostazioniOpen] = useState(false);
  const [agenzieAperte, setAgenzieAperte] = useState({});
  const [gruppiAperti, setGruppiAperti] = useState({});
  const [vista, setVista] = useState(() => localStorage.getItem("dbe-vista") || "auto");
  const cambiaVista = (v) => {
    setVista(v);
    localStorage.setItem("dbe-vista", v);
  };
  const [compatta, setCompatta] = useState(() => localStorage.getItem("dbe-sidebar") === "compatta");
  const riduci = (v) => {
    setCompatta(v);
    localStorage.setItem("dbe-sidebar", v ? "compatta" : "estesa");
  };
  const forzaDesktop = vista === "desktop";
  const forzaMobile = vista === "mobile";
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [navigate]);

  useEffect(() => {
    api.get("/commerciali").then((r) => setComms(
      (r.data.commerciali || []).filter((c) => c.slug !== "altro")
        .sort((a, b) => (a.slug === "gestione-hub" ? 1 : b.slug === "gestione-hub" ? -1
          : a.nome.localeCompare(b.nome, "it")))
    )).catch(() => setComms([]));
    const caricaMenu = () => api.get("/menu-struttura")
      .then((r) => setVociCustom(r.data.voci || [])).catch(() => setVociCustom([]));
    caricaMenu();
    api.get("/config").then((r) => {
      const p = (r.data.rows || []).find((c) => c.key === "palette_colori");
      if (p) applicaPalette(p.value);
    }).catch(() => {});
    window.addEventListener("menu-struttura-changed", caricaMenu);
    return () => window.removeEventListener("menu-struttura-changed", caricaMenu);
  }, []);

  useEffect(() => {
    if (canale !== "corto") return;
    api.get("/corto/agenzie").then((r) => setAgenzieCorto(r.data.rows || []))
      .catch(() => setAgenzieCorto([]));
  }, [canale]);

  const items = canale === "corto"
    ? [{ to: "/canale-corto", label: "Dashboard corto", icon: BarChart3, exact: true, pagina: null }]
    : NAV.filter((n) => can(n.pagina));
  const pagineDi = (parent) => vociCustom
    .filter((v) => v.tipo === "pagina" && (v.parent || "impostazioni") === parent);
  const gruppiCustom = vociCustom.filter((v) => v.tipo === "gruppo");
  const masterOAdmin = ["MASTER", "ADMIN"].includes(user?.role);
  const slugConsentiti = masterOAdmin ? null : (user?.permissions?.commerciali_visibili?.length
    ? user.permissions.commerciali_visibili
    : (user?.commerciale_slug ? [user.commerciale_slug] : null));
  const commercialiVisibili = slugConsentiti
    ? comms.filter((c) => slugConsentiti.includes(c.slug))
    : comms;
  const iniziali = ((user?.name || user?.email || "?").match(/\p{L}+/gu) || ["?"])
    .slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  const sezioniMobile = canale === "corto" ? [
    can("corto_dashboard") && { key: "corto-dashboard", label: "Dashboard corto", breve: "Dashboard", icon: BarChart3,
      to: "/canale-corto",
      voci: can("corto_tassi") ? [{ to: "/canale-corto/tassi-premi",
        label: "Tassi, premi e redditività", testid: "corto-tassi-premi" }] : [] },
    (can("corto_classifiche_caricato") || can("corto_classifiche_liquidato")
      || can("corto_classifiche_tassi")) && { key: "corto-classifiche", label: "Classifiche",
      icon: Trophy,
      voci: [
        ...(can("corto_classifiche_caricato") ? [{ to: "/canale-corto/classifiche/caricato",
          label: "Classifiche caricato", testid: "corto-classifiche-caricato" }] : []),
        ...(can("corto_classifiche_liquidato") ? [{ to: "/canale-corto/classifiche/liquidato",
          label: "Classifiche liquidato", testid: "corto-classifiche-liquidato" }] : []),
        ...(can("corto_classifiche_tassi") ? [{ to: "/canale-corto/classifiche/tassi",
          label: "Classifiche tassi, premi, redditività", testid: "corto-classifiche-tassi" }] : [])] },
    can("corto_agenzie") && { key: "corto-agenzie", label: "Agenzie", icon: Building2,
      voci: agenzieCorto.flatMap((a) => [
        { to: `/canale-corto/agenzie/${a.slug}`, label: a.agenzia, testid: `corto-${a.slug}` },
        ...(can("corto_tassi") ? [{ to: `/canale-corto/agenzie/${a.slug}/tassi-premi`,
          label: `${a.agenzia} · tassi, premi e redditività`,
          testid: `corto-${a.slug}-tassi` }] : [])]) },
    can("corto_telefonate") && { key: "corto-telefonate", label: "Analisi Telefonate", breve: "Telefonate", icon: Phone,
      to: "/canale-corto/telefonate", voci: [] },
    can("corto_report_agenzia") && { key: "corto-report-agenzia", label: "Report agenzia", breve: "Report", icon: FileText,
      to: "/canale-corto/report-agenzia", voci: [] },
    ...(can("corto_configurazioni") || can("corto_utenti")
      ? [{ key: "corto-impostazioni", label: "Impostazioni", icon: Settings,
           voci: [
             ...(can("corto_configurazioni") ? [{ to: "/canale-corto/configurazioni",
               label: "Configurazioni", testid: "corto-configurazioni" },
               { to: "/canale-corto/proiezioni", label: "Proiezioni e previsioni", testid: "corto-proiezioni" }] : []),
             ...(can("corto_utenti") ? [{ to: "/canale-corto/utenti", label: "Utenti",
               testid: "corto-utenti" }] : [])] }]
      : []),
  ].filter(Boolean) : [
    can("home") && { key: "home", label: "Home", icon: Home, voci: [
      { to: "/", label: "Dashboard 2026", exact: true, testid: "home-dashboard" },
      { to: "/portafoglio-sviluppo", label: "Portafoglio e Sviluppo", testid: "portafoglio-sviluppo" },
      { to: "/classifiche-dealer", label: "Classifiche Dealer", testid: "classifiche-dealer" },
      ...(can("performance") ? [{ to: "/performance", label: "Performance commerciali", testid: "performance" }] : []),
      { to: "/performance-scostamenti", label: "Scostamento vs 2025", testid: "scostamenti" },
      { to: "/monitoraggio", label: "Monitoraggio 2026", testid: "monitoraggio" },
      ...pagineDi("home").map((p) => ({ to: `/p/${p.slug}`, label: p.nome, testid: p.slug })),
    ] },
    can("commerciali") && { key: "commerciali", label: "Commerciali", icon: BarChart3,
      voci: commercialiVisibili.map((c) => ({
        label: c.nome, testid: c.slug, sotto: [
          { to: `/commerciali/${c.slug}`, label: "Dashboard 2026", exact: true, testid: `${c.slug}-dashboard` },
          { to: `/commerciali/${c.slug}/portafoglio-sviluppo`, label: "Portafoglio e Sviluppo",
            testid: `${c.slug}-portafoglio-sviluppo` },
          ...(can("analisi_portafoglio") ? [{ to: `/commerciali/${c.slug}/analisi-portafoglio`,
            label: "Analisi Portafoglio", testid: `${c.slug}-analisi-portafoglio` }] : []),
          { to: `/commerciali/${c.slug}/classifiche-dealer`, label: "Classifiche Dealer",
            testid: `${c.slug}-classifiche` },
          { to: `/commerciali/${c.slug}/performance`, label: "Scostamento vs 2025",
            testid: `${c.slug}-scostamenti` },
          { to: `/commerciali/${c.slug}/giornaliero`, label: "Pratiche giornaliere",
            testid: `${c.slug}-giornaliero` },
          ...(c.zona?.length ? [{ to: `/commerciali/${c.slug}/mappa-zona`, label: "Mappa zona",
            testid: `${c.slug}-mappa-zona` }] : []),
          ...pagineDi("home").filter((p) => p.per_commerciale)
            .map((p) => ({ to: `/commerciali/${c.slug}/p/${p.slug}`, label: p.nome,
              testid: `${c.slug}-${p.slug}` })),
        ],
      })) },
    can("giornaliero") && { key: "giornaliero", label: "Pratiche giornaliere",
      breve: "Giornaliero", icon: CalendarDays, to: "/giornaliero" },
    can("analisi_portafoglio") && { key: "analisi-portafoglio", label: "Analisi Portafoglio",
      breve: "Portafoglio", icon: PieChart, to: "/analisi-portafoglio" },
    can("convenzionati") && { key: "convenzionati", label: "Convenzionati", breve: "Dealer", icon: Building2, voci: [
      { to: "/convenzionati", label: "Anagrafiche", testid: "anagrafiche" },
      ...(can("classifiche") ? [{ to: "/classifiche-convenzionati", label: "Classifiche", testid: "classifiche" }] : []),
      ...(can("mappa") ? [{ to: "/mappa-convenzionati", label: "Mappa", testid: "mappa" }] : []),
    ] },
    can("pratiche") && { key: "pratiche", label: "Pratiche", icon: FileStack, to: "/pratiche" },
    can("news") && { key: "news", label: "News", icon: Newspaper, to: "/news" },
    can("impostazioni") && { key: "impostazioni", label: "Impostazioni", icon: Settings, voci: [
      { to: "/impostazioni", label: "Configurazioni", exact: true, testid: "configurazioni" },
      { to: "/impostazioni/proiezioni", label: "Proiezioni e previsioni", testid: "proiezioni" },
      ...(can("importazioni") ? [{ to: "/importazioni", label: "Importazioni", testid: "importazioni" }] : []),
      ...(can("formule") ? [{ to: "/formule-grafici", label: "Formule e grafici", testid: "formule" }] : []),
      ...(can("utenti") ? [{ to: "/utenti", label: "Utenti", testid: "utenti" }] : []),
      ...(can("archivio") ? [{ to: "/archivio", label: "Archivio", testid: "archivio" }] : []),
      ...pagineDi("impostazioni").filter((p) => p.slug !== "archivio")
        .map((p) => ({ to: `/p/${p.slug}`, label: p.nome, testid: p.slug })),
    ] },
    ...gruppiCustom.map((g) => ({ key: g.slug, label: g.nome, icon: FileStack,
      voci: pagineDi(g.id).map((p) => ({ to: `/p/${p.slug}`, label: p.nome, testid: p.slug })) })),
  ].filter(Boolean);

  const linkCls = ({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200
    ${isActive ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"}`;

  return (
    <div className="min-h-screen text-slate-900" style={{ background: SFONDO_PAGINE }}>
      <aside className={`fixed z-40 inset-y-0 left-0 bg-white border-r border-slate-200 flex-col
        transition-[width] duration-200 ${compatta ? "w-16" : "w-64"}
        ${forzaDesktop ? "flex" : forzaMobile ? "hidden" : "hidden lg:flex"}`}>
        <div className={`h-16 flex items-center border-b border-slate-200
          ${compatta ? "justify-center px-2" : "justify-between px-6"}`}>
          {!compatta && (
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <img src="/dbe-logo.png" alt="DBE Consulenza Finanziaria"
                className="h-8 w-auto" data-testid="brand-logo" />
            </Link>
          )}
          <button type="button" onClick={() => riduci(!compatta)} data-testid="sidebar-riduci"
            title={compatta ? "Espandi menu" : "Riduci menu"}
            className="text-slate-400 hover:text-slate-900 transition-colors duration-200">
            {compatta ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        {compatta ? (
          <nav className="flex-1 overflow-y-auto py-4 space-y-1" data-testid="sidebar-compatta">
            {sezioniMobile.map((sez) => (sez.to ? (
              <NavLink key={sez.key} to={sez.to} title={sez.label} data-testid={`rail-${sez.key}`}
                className={({ isActive }) => `mx-2 h-11 grid place-items-center rounded-md
                  transition-colors duration-200 ${isActive
                    ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                <sez.icon size={18} />
              </NavLink>
            ) : (
              <button key={sez.key} type="button" title={sez.label} onClick={() => riduci(false)}
                data-testid={`rail-${sez.key}`}
                className="w-full px-2 flex justify-center">
                <span className="h-11 w-full grid place-items-center rounded-md text-slate-500
                  hover:bg-slate-100 transition-colors duration-200"><sez.icon size={18} /></span>
              </button>
            )))}
          </nav>
        ) : (
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n, i) => (
            <div key={n.to}>
              {n.to === "/" || n.to === "/impostazioni" ? (
                <button type="button" data-testid={`nav-${n.to === "/" ? "home" : "impostazioni"}-toggle`}
                  onClick={() => (n.to === "/" ? setHomeOpen((v) => !v) : setSetOpen((v) => !v))}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200
                    text-slate-600 hover:bg-slate-50 hover:text-slate-900`}>
                  <n.icon size={16} /> {n.label}
                  <ChevronDown size={14} className={`ml-auto transition-transform duration-200
                    ${(n.to === "/" ? homeOpen : setOpenState) ? "rotate-180" : ""}`} />
                </button>
              ) : (
                <NavLink to={n.to} end={n.exact} className={linkCls}
                  data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                  onClick={() => setOpen(false)}>
                  <n.icon size={16} /> {n.label}
                </NavLink>
              )}
              {n.to === "/" && homeOpen && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1" data-testid="nav-home-sub">
                  <NavLink to="/" end className={linkCls} data-testid="nav-home-dashboard"
                    onClick={() => setOpen(false)}>Dashboard 2026</NavLink>
                  <NavLink to="/portafoglio-sviluppo" className={linkCls} data-testid="nav-home-portafoglio-sviluppo"
                    onClick={() => setOpen(false)}>Portafoglio e Sviluppo</NavLink>
                  <NavLink to="/classifiche-dealer" className={linkCls} data-testid="nav-home-classifiche-dealer"
                    onClick={() => setOpen(false)}>Classifiche Dealer</NavLink>
                  {can("performance") && (
                    <NavLink to="/performance" className={linkCls} data-testid="nav-performance"
                      onClick={() => setOpen(false)}>Performance commerciali</NavLink>
                  )}
                  <NavLink to="/performance-scostamenti" className={linkCls} data-testid="nav-home-performance"
                    onClick={() => setOpen(false)}>Scostamento vs 2025</NavLink>
                  <NavLink to="/monitoraggio" className={linkCls} data-testid="nav-home-monitoraggio"
                    onClick={() => setOpen(false)}>Monitoraggio 2026</NavLink>
                  {pagineDi("home").map((p) => (
                    <NavLink key={p.id} to={`/p/${p.slug}`} className={linkCls}
                      data-testid={`nav-custom-${p.slug}`} onClick={() => setOpen(false)}>{p.nome}</NavLink>
                  ))}
                </div>
              )}
              {n.to === (can("analisi_portafoglio") ? "/analisi-portafoglio" : "/giornaliero") && can("convenzionati") && (
                <div className="mt-1">
                  <button type="button" data-testid="nav-convenzionati-toggle"
                    onClick={() => setDealerOpen((v) => !v)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200
                      ${dealerOpen ? "text-slate-900" : "text-slate-500"} hover:bg-slate-50 hover:text-slate-900`}>
                    <Building2 size={16} /> Convenzionati
                    <ChevronDown size={14} className={`ml-auto transition-transform duration-200 ${dealerOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dealerOpen && (
                    <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1" data-testid="nav-convenzionati-list">
                      <NavLink to="/convenzionati" className={linkCls} data-testid="nav-convenzionati-anagrafiche"
                        onClick={() => setOpen(false)}>Anagrafiche</NavLink>
                      {can("classifiche") && (
                        <NavLink to="/classifiche-convenzionati" className={linkCls} data-testid="nav-convenzionati-classifiche"
                          onClick={() => setOpen(false)}>Classifiche</NavLink>
                      )}
                      {can("mappa") && (
                        <NavLink to="/mappa-convenzionati" className={linkCls} data-testid="nav-convenzionati-mappa"
                          onClick={() => setOpen(false)}>Mappa</NavLink>
                      )}
                    </div>
                  )}
                </div>
              )}
              {n.to === "/impostazioni" && setOpenState && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1" data-testid="nav-impostazioni-sub">
                  <NavLink to="/impostazioni" end className={linkCls} data-testid="nav-configurazioni"
                    onClick={() => setOpen(false)}>Configurazioni</NavLink>
                  <NavLink to="/impostazioni/proiezioni" className={linkCls} data-testid="nav-proiezioni"
                    onClick={() => setOpen(false)}>Proiezioni e previsioni</NavLink>
                  {can("importazioni") && (
                    <NavLink to="/importazioni" className={linkCls} data-testid="nav-importazioni"
                      onClick={() => setOpen(false)}>Importazioni</NavLink>
                  )}
                  {can("formule") && (
                    <NavLink to="/formule-grafici" className={linkCls} data-testid="nav-formule-grafici"
                      onClick={() => setOpen(false)}>Formule e grafici</NavLink>
                  )}
                  {can("utenti") && (
                    <NavLink to="/utenti" className={linkCls} data-testid="nav-utenti"
                      onClick={() => setOpen(false)}>Utenti</NavLink>
                  )}
                  {can("archivio") && (
                    <NavLink to="/archivio" className={linkCls} data-testid="nav-archivio"
                      onClick={() => setOpen(false)}>Archivio</NavLink>
                  )}
                  {pagineDi("impostazioni").filter((p) => p.slug !== "archivio").map((p) => (
                    <NavLink key={p.id} to={`/p/${p.slug}`} className={linkCls}
                      data-testid={`nav-custom-${p.slug}`} onClick={() => setOpen(false)}>{p.nome}</NavLink>
                  ))}
                </div>
              )}
              {i === 0 && canale !== "corto" && can("commerciali") && (
                <div className="mt-1">
                  <button type="button" data-testid="nav-commerciali-toggle"
                    onClick={() => setCommOpen((v) => !v)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200
                      ${commOpen ? "text-slate-900" : "text-slate-500"} hover:bg-slate-50 hover:text-slate-900`}>
                    <BarChart3 size={16} /> Commerciali
                    <ChevronDown size={14} className={`ml-auto transition-transform duration-200 ${commOpen ? "rotate-180" : ""}`} />
                  </button>
                  {commOpen && (
                    <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1"
                      data-testid="nav-commerciali-list">
                      {commercialiVisibili.map((c) => (
                        <div key={c.slug}>
                          <button type="button" data-testid={`nav-commerciale-toggle-${c.slug}`}
                            onClick={() => setCommAperti((v) => ({ ...v, [c.slug]: !v[c.slug] }))}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors duration-200
                              ${commAperti[c.slug] ? "text-slate-900" : "text-slate-500"} hover:bg-slate-50 hover:text-slate-900`}>
                            <span className="truncate whitespace-nowrap" title={c.nome}>{c.nome}</span>
                            <ChevronDown size={14} className={`ml-auto shrink-0 transition-transform duration-200
                              ${commAperti[c.slug] ? "rotate-180" : ""}`} />
                          </button>
                          {commAperti[c.slug] && (
                            <div className="mt-1 ml-3 pl-3 border-l border-slate-200 space-y-1">
                              <NavLink to={`/commerciali/${c.slug}`} end className={linkCls}
                                data-testid={`nav-commerciali/${c.slug}`}
                                onClick={() => setOpen(false)}>Dashboard 2026</NavLink>
                              <NavLink to={`/commerciali/${c.slug}/portafoglio-sviluppo`} className={linkCls}
                                data-testid={`nav-commerciali/${c.slug}/portafoglio-sviluppo`}
                                onClick={() => setOpen(false)}>Portafoglio e Sviluppo</NavLink>
                              {can("analisi_portafoglio") && (
                                <NavLink to={`/commerciali/${c.slug}/analisi-portafoglio`} className={linkCls}
                                  data-testid={`nav-commerciali/${c.slug}/analisi-portafoglio`}
                                  onClick={() => setOpen(false)}>Analisi Portafoglio</NavLink>
                              )}
                              <NavLink to={`/commerciali/${c.slug}/classifiche-dealer`} className={linkCls}
                                data-testid={`nav-commerciali/${c.slug}/classifiche-dealer`}
                                onClick={() => setOpen(false)}>Classifiche Dealer</NavLink>
                              <NavLink to={`/commerciali/${c.slug}/performance`} className={linkCls}
                                data-testid={`nav-commerciali/${c.slug}/performance`}
                                onClick={() => setOpen(false)}>Scostamento vs 2025</NavLink>
                              <NavLink to={`/commerciali/${c.slug}/giornaliero`} className={linkCls}
                                data-testid={`nav-commerciali/${c.slug}/giornaliero`}
                                onClick={() => setOpen(false)}>Pratiche giornaliere</NavLink>
                              {c.zona?.length > 0 && (
                                <NavLink to={`/commerciali/${c.slug}/mappa-zona`} className={linkCls}
                                  data-testid={`nav-commerciali/${c.slug}/mappa-zona`}
                                  onClick={() => setOpen(false)}>Mappa zona</NavLink>
                              )}
                              {pagineDi("home").filter((p) => p.per_commerciale).map((p) => (
                                <NavLink key={p.id} to={`/commerciali/${c.slug}/p/${p.slug}`} className={linkCls}
                                  data-testid={`nav-custom-${c.slug}-${p.slug}`}
                                  onClick={() => setOpen(false)}>{p.nome}</NavLink>
                              ))}

                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {canale === "corto" && (
            <div data-testid="nav-corto-agenzie">
              {can("corto_tassi") && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1"
                  data-testid="nav-corto-dashboard-sub">
                  <NavLink to="/canale-corto/tassi-premi" className={linkCls}
                    data-testid="nav-corto-tassi-premi" onClick={() => setOpen(false)}>
                    Tassi, premi e redditività
                  </NavLink>
                </div>
              )}
              {(can("corto_classifiche_caricato") || can("corto_classifiche_liquidato")
                || can("corto_classifiche_tassi")) && (
              <>
              <button type="button" data-testid="nav-corto-classifiche-toggle"
                onClick={() => setClassificheOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-600
                  hover:bg-slate-50 hover:text-slate-900 transition-colors duration-200">
                <Trophy size={16} /> Classifiche
                <ChevronDown size={14} className={`ml-auto transition-transform duration-200
                  ${classificheOpen ? "rotate-180" : ""}`} />
              </button>
              {classificheOpen && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1"
                  data-testid="nav-corto-classifiche-list">
                  {[["caricato", "Classifiche caricato"], ["liquidato", "Classifiche liquidato"],
                    ["tassi", "Classifiche tassi, premi, redditività"]]
                    .filter(([v]) => can(`corto_classifiche_${v}`)).map(([v, label]) => (
                    <NavLink key={v} to={`/canale-corto/classifiche/${v}`} className={linkCls}
                      data-testid={`nav-corto-classifiche-${v}`} onClick={() => setOpen(false)}>
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
              </>
              )}
              {can("corto_agenzie") && (
              <>
              <button type="button" data-testid="nav-corto-agenzie-toggle"
                onClick={() => setAgenzieOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-600
                  hover:bg-slate-50 hover:text-slate-900 transition-colors duration-200">
                <Building2 size={16} /> Agenzie
                <ChevronDown size={14} className={`ml-auto transition-transform duration-200
                  ${agenzieOpen ? "rotate-180" : ""}`} />
              </button>
              {agenzieOpen && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1"
                  data-testid="nav-corto-agenzie-list">
                  {agenzieCorto.map((a) => (
                    <div key={a.slug}>
                      <div className="flex items-center gap-1">
                        <NavLink to={`/canale-corto/agenzie/${a.slug}`} className={`${linkCls} flex-1`}
                          data-testid={`nav-corto-agenzia-${a.slug}`}
                          onClick={() => setOpen(false)}>{a.agenzia}</NavLink>
                        <button type="button" data-testid={`nav-corto-agenzia-toggle-${a.slug}`}
                          onClick={() => setAgenzieAperte((v) => ({ ...v, [a.slug]: !v[a.slug] }))}
                          className="p-1 rounded-md text-slate-400 hover:text-slate-700
                            hover:bg-slate-50 transition-colors duration-200">
                          <ChevronDown size={13} className={`transition-transform duration-200
                            ${agenzieAperte[a.slug] ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                      {agenzieAperte[a.slug] && can("corto_tassi") && (
                        <div className="mt-1 ml-3 pl-3 border-l border-slate-200">
                          <NavLink to={`/canale-corto/agenzie/${a.slug}/tassi-premi`}
                            className={linkCls} onClick={() => setOpen(false)}
                            data-testid={`nav-corto-agenzia-tassi-${a.slug}`}>
                            Tassi, premi e redditività
                          </NavLink>
                        </div>
                      )}
                    </div>
                  ))}
                  {!agenzieCorto.length && (
                    <div className="px-3 py-2 text-xs text-slate-400">Nessuna agenzia</div>
                  )}
                </div>
              )}
              </>
              )}
              {can("corto_report_agenzia") && (
                <NavLink to="/canale-corto/report-agenzia" data-testid="nav-corto-report-agenzia"
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm
                    transition-colors duration-200 ${isActive ? "bg-slate-100 text-slate-900 font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  <FileText size={16} /> Report agenzia
                </NavLink>
              )}
              {can("corto_telefonate") && (
                <NavLink to="/canale-corto/telefonate" data-testid="nav-corto-telefonate"
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm
                    transition-colors duration-200 ${isActive ? "bg-slate-100 text-slate-900 font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  <Phone size={16} /> Analisi Telefonate
                </NavLink>
              )}
              {(can("corto_configurazioni") || can("corto_utenti")) && (
                <>
                  <button type="button" data-testid="nav-corto-impostazioni-toggle"
                    onClick={() => setImpostazioniOpen((v) => !v)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-600
                      hover:bg-slate-50 hover:text-slate-900 transition-colors duration-200">
                    <Settings size={16} /> Impostazioni
                    <ChevronDown size={14} className={`ml-auto transition-transform duration-200
                      ${impostazioniOpen ? "rotate-180" : ""}`} />
                  </button>
                  {impostazioniOpen && (
                    <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1"
                      data-testid="nav-corto-impostazioni-list">
                      {can("corto_configurazioni") && (
                        <NavLink to="/canale-corto/configurazioni" className={linkCls}
                          data-testid="nav-corto-configurazioni" onClick={() => setOpen(false)}>
                          Configurazioni
                        </NavLink>
                      )}
                      {can("corto_configurazioni") && (
                        <NavLink to="/canale-corto/proiezioni" className={linkCls}
                          data-testid="nav-corto-proiezioni" onClick={() => setOpen(false)}>
                          Proiezioni e previsioni
                        </NavLink>
                      )}
                      {can("corto_utenti") && (
                        <NavLink to="/canale-corto/utenti" className={linkCls}
                          data-testid="nav-corto-utenti" onClick={() => setOpen(false)}>
                          Utenti
                        </NavLink>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {gruppiCustom.map((g) => (
            <div key={g.id} data-testid={`nav-gruppo-${g.slug}`}>
              <button type="button" data-testid={`nav-gruppo-toggle-${g.slug}`}
                onClick={() => setGruppiAperti((v) => ({ ...v, [g.id]: !v[g.id] }))}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-600
                  hover:bg-slate-50 hover:text-slate-900 transition-colors duration-200">
                <FileStack size={16} /> {g.nome}
                <ChevronDown size={14} className={`ml-auto transition-transform duration-200
                  ${gruppiAperti[g.id] ? "rotate-180" : ""}`} />
              </button>
              {gruppiAperti[g.id] && (
                <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1">
                  {pagineDi(g.id).map((p) => (
                    <NavLink key={p.id} to={`/p/${p.slug}`} className={linkCls}
                      data-testid={`nav-custom-${p.slug}`} onClick={() => setOpen(false)}>{p.nome}</NavLink>
                  ))}
                  {!pagineDi(g.id).length && (
                    <div className="px-3 py-2 text-xs text-slate-400">Nessuna pagina</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>
        )}
        {!compatta && (
          <div className="border-t border-slate-200 p-4">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-slate-400 truncate">{user?.email} · {user?.role}</div>
          </div>
        )}
      </aside>

      <div className={forzaMobile ? "pl-0"
        : forzaDesktop ? (compatta ? "pl-16" : "pl-64")
        : compatta ? "lg:pl-16" : "lg:pl-64"}>
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-4 px-4 sm:px-8 sticky top-0 z-20">
          <Link to="/" className={forzaDesktop ? "hidden" : forzaMobile ? "block" : "lg:hidden"}
            data-testid="brand-link-mobile">
            <img src="/dbe-logo.png" alt="DBE" className="h-7 w-auto" />
          </Link>
          <div className="hidden sm:block text-sm text-slate-500">Agenzia DBE · Credito al consumo</div>
          <div className="ml-auto flex items-center gap-0.5 rounded-full border border-slate-200 p-0.5
            sm:gap-1 sm:p-1" data-testid="switch-vista">
              {[["auto", "Auto", null], ["desktop", "Desktop", Monitor], ["mobile", "Mobile", Smartphone]]
                .map(([k, label, Icona]) => (
                  <button key={k} type="button" title={`Vista ${label}`} onClick={() => cambiaVista(k)}
                    data-testid={`switch-vista-${k}`}
                    className={`px-2 py-1 rounded-full text-[11px] sm:px-2.5 sm:text-xs
                      transition-colors duration-200
                      ${vista === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
                    {Icona ? <Icona size={14} /> : label}
                  </button>
                ))}
          </div>
          {onCambiaCanale && (
            <button type="button" onClick={onCambiaCanale} data-testid="btn-cambia-canale"
              title="Cambia canale"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-md border text-xs
                font-semibold uppercase tracking-[0.14em] transition-colors duration-200
                border-slate-200 text-slate-600 hover:border-[#C8102E] hover:text-[#C8102E]">
              <span className="h-2 w-2 rounded-full"
                style={{ background: canale === "corto" ? "#0018A8" : "#C8102E" }} />
              Canale {canale}
            </button>
          )}
          {user?.role !== "VISITATORE" && <CampanellaNotifiche />}
          <div className="relative">
            <button type="button" data-testid="account-menu-btn" onClick={() => setAccountOpen((v) => !v)}
              title="Account"
              className="h-10 w-10 grid place-items-center overflow-hidden rounded-full bg-[#0018A8]
                text-white text-sm font-semibold tracking-wide hover:brightness-110 transition-all duration-200">
              {user?.avatar
                ? <img src={user.avatar} alt="Profilo" data-testid="account-avatar-header"
                    className="h-full w-full object-cover" />
                : <span data-testid="account-initials">{iniziali}</span>}
            </button>
            {accountOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-4 z-30"
                data-testid="account-menu">
                <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 mt-1">{user?.role}</div>
                <Link to="/account" data-testid="account-menu-link" onClick={() => setAccountOpen(false)}
                  className="mt-4 block text-sm px-3 py-2 rounded-md bg-slate-900 text-white text-center
                    font-medium hover:bg-slate-700 transition-colors duration-200">
                  Nome utente e password
                </Link>
                <button type="button" data-testid="account-menu-logout"
                  onClick={() => { setAccountOpen(false); logout(); }}
                  className="mt-2 w-full text-sm px-3 py-2 rounded-md border border-slate-200 text-slate-600
                    hover:border-red-400 hover:text-red-600 transition-colors duration-200">
                  Esci
                </button>
              </div>
            )}
          </div>
        </header>
        {!forzaDesktop && <MenuMobile sezioni={sezioniMobile} sempreVisibile={forzaMobile} />}
        <main className="p-4 sm:p-8 max-w-[1700px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
